'use client';

import Image from 'next/image';
import { SearchDialog } from '../dialogs/search-dialog';
import { signIn, useSession } from 'next-auth/react';
import UserPopover from '../popover/user-popover';
import { Button } from '../ui/button';
import { Heart, UserIcon } from 'lucide-react';
import { Link, usePathname } from '@/src/i18n/navigation';
import { ChangeLang } from '../change-lang';
import { useLocale, useTranslations } from 'next-intl';
import { AddressSheet } from '../dialogs/address-sheet';
import logo from '@/public/logo.svg';
import { useBreadcrumbsStore } from '@/src/lib/state-stores/breadcrumbStore';
import { Breadcrumbs } from '../breadcrumbs/Breadcrumbs';
import { useLocationStore } from '@/src/lib/state-stores/locationStore';
import { Session } from 'next-auth';
import { Routes } from '@/src/routes/routes';
import usePrimeStore from '@/src/lib/state-stores/primeStore';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import UserCart from './UserCard';
import Container from '../ui/container';
import Typography from '../ui/typography';
import AsyncSearchCombobox from '../SearchAutocomplete';

export type TSession = {
  session?: Session | null;
};

const HeaderDesktop = ({ session: serverSession }: TSession) => {
  const { data: clientSession } = useSession();
  const session = serverSession ?? clientSession;
  const { locationDetails } = useLocationStore();
  const { prime } = usePrimeStore();

  const latitude = locationDetails?.location?.latitude ?? '24.7438193';
  const longitude = locationDetails?.location?.longitude ?? '46.7039281';
  const currentPath = usePathname();
  const isStoreRoute = currentPath === '/stores' || /^\/stores\/\d+$/.test(currentPath);
  const isDigitalPath = currentPath === Routes.digitalCards;

  const localActive = useLocale();
  // const isSotresPage = currentPath.includes('/stores');

  const checkIfHome = currentPath === `/`;

  const t = useTranslations('header');

  return (
    <nav className="mb-4 hidden flex-col items-center justify-start leading-0 transition-colors duration-300 lg:flex">
      <Container className="flex w-full items-center justify-between py-6">
        {/* logo and address  */}
        <div className="flex flex-row items-center space-x-6">
          <Link
            href="/"
            className="self-end"
            onClick={() => trackEvent(AmplitudeEvents.home_app_clicked)}
          >
            <Image priority src={logo} alt="logo" className="w-20" />
          </Link>

          <AddressSheet
            lang={localActive === 'ar' ? 'ar' : 'en'}
            defaultLatitude={latitude.toString()}
            defaultLongitude={longitude.toString()}
          />

          <ChangeLang />
        </div>

        {/* search bar  */}
        <div
          onClick={() => trackEvent(AmplitudeEvents.search_bar_clicked)}
          className="flex h-12 w-2/5 items-center justify-center"
        >
          {!isStoreRoute && !isDigitalPath ? (
            // <SearchDialog
            //   isSotresPage={isStoreRoute}
            //   lang={localActive === 'en' ? 'en' : 'ar'}
            //   defaultLatitude={latitude.toString()}
            //   defaultLongitude={longitude.toString()}
            //   className="flex rounded-xl"
            //   mediaClass="hidden lg:block"
            //   dialogClassName="top-[48%] ltr:start-[50.8%] rtl:end-[50.8%] rounded-3xl hidden lg:flex"
            // />
            <AsyncSearchCombobox />
          ) : // <AsyncAutocomplete />
          null}
        </div>

        {/* user  */}
        <div className="flex items-center space-x-4 rtl:space-x-reverse">
          <div className="mt-1 flex items-center gap-4">
            {session && session?.user ? (
              <>
                <UserPopover>
                  <div className="text-primary flex cursor-pointer flex-row items-center space-x-3 text-base font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
                    <div className="flex max-w-45 gap-2">
                      <Typography variant={'h6'} truncate weight={'bold'}>
                        {t('hello')}, {session?.fullName}
                      </Typography>
                      {prime?.primeEnabled && prime?.activeSubscription ? (
                        <Image
                          src={'/assets/prime.svg'}
                          alt="order"
                          width={'20'}
                          height={'21'}
                          className="h-5.25 w-5"
                        />
                      ) : null}
                    </div>
                    <UserIcon />
                  </div>
                </UserPopover>
                <Link
                  href={Routes.wishlist}
                  onClick={() => trackEvent(AmplitudeEvents.my_wishlist_clicked)}
                >
                  <Heart />
                </Link>

                {/* cart */}
                <UserCart session={session} />
              </>
            ) : (
              <div className="flex items-center">
                <Button
                  id="login-button"
                  variant={'link'}
                  className="text-primary flex flex-row items-center justify-center space-x-2 rtl:space-x-reverse"
                  onClick={() => signIn('keycloak')}
                >
                  <Typography variant={'h6'} weight={'black'}>
                    {t('login')}
                  </Typography>
                  <UserIcon />
                </Button>
                <Link
                  href={Routes.joinUs}
                  onClick={() => trackEvent(AmplitudeEvents.join_us)}
                  className="text-primary/70 hover:text-primary shrink-0 font-bold hover:underline"
                >
                  <Typography variant={'subtitle1'} weight={'black'}>
                    {t('Join-us')}
                  </Typography>
                </Link>
              </div>
            )}
          </div>
        </div>
      </Container>

      {/* menu  */}
      <ul className="mx-auto hidden w-full flex-row items-center justify-center space-x-6 border-y border-[#E7E7E8] py-4 lg:flex rtl:space-x-reverse">
        <li>
          <Link
            prefetch={false}
            className={`${
              checkIfHome ? 'text-primary' : 'text-primary/50'
            } hover:text-primary flex items-center rounded-lg p-3 pb-2 text-xl leading-0 font-extrabold uppercase transition-all`}
            href="/"
            onClick={() => trackEvent(AmplitudeEvents.home_app_clicked)}
          >
            {t('home')}
          </Link>
        </li>
        <li>
          <Link
            prefetch={false}
            className={`${
              currentPath === '/stores' ? 'text-primary' : 'text-primary/50'
            } hover:text-primary flex items-center rounded-lg p-3 pb-2 text-xl leading-0 font-extrabold uppercase transition-all`}
            href={'/stores'}
            onClick={() => trackEvent(AmplitudeEvents.stores_clicked)}
          >
            {t('stores')}
          </Link>
        </li>
      </ul>
    </nav>
  );
};

const HeaderMobile = ({ session: serverSession }: TSession) => {
  const { data: clientSession } = useSession();
  const session = serverSession ?? clientSession;
  const { locationDetails } = useLocationStore();

  const latitude = locationDetails?.location?.latitude ?? '24.7438193';
  const longitude = locationDetails?.location?.longitude ?? '46.7039281';
  const localActive = useLocale();
  const currentPath = usePathname();
  const isStoreRoute = currentPath === '/stores' || /^\/stores\/\d+$/.test(currentPath);
  // const isSotresPage = currentPath.includes('/stores');
  const isDigitalPath = currentPath === Routes.digitalCards;

  const t = useTranslations('header');

  return (
    <nav className={`z-50 flex flex-col rounded-b py-4 backdrop-blur-3xl lg:hidden`}>
      <Container className="w-full">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="text-primary flex flex-row items-center space-x-3 text-lg font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
            <p className="text-base">
              {session && session.user ? (
                <UserPopover>
                  <div className="text-md text-primary flex flex-row items-center space-x-3 font-extrabold transition-opacity hover:opacity-50 rtl:space-x-reverse">
                    <p className={'text-sm'}>
                      {t('hello')}, {session?.fullName} ✨
                    </p>
                  </div>
                </UserPopover>
              ) : null}
            </p>
          </div>

          <AddressSheet
            lang={localActive === 'ar' ? 'ar' : 'en'}
            defaultLatitude={latitude.toString()}
            defaultLongitude={longitude.toString()}
          />
        </div>

        {!isStoreRoute && !isDigitalPath ? (
          // <SearchDialog
          //   lang={localActive === 'en' ? 'en' : 'ar'}
          //   isSotresPage={isStoreRoute}
          //   className="mt-2 flex rounded-2xl px-2"
          //   mediaClass=" block"
          //   dialogClassName="top-[46%] ltr:start-[50%] rtl:end-[50%] rounded-2xl flex"
          //   defaultLatitude={latitude.toString()}
          //   defaultLongitude={longitude.toString()}
          // />
          <AsyncSearchCombobox />
        ) : // <AsyncAutocomplete />
        null}
      </Container>
    </nav>
  );
};
const Navbar = ({ session }: TSession) => {
  const { breadcrumbItems } = useBreadcrumbsStore();

  return (
    <>
      <HeaderDesktop session={session} />
      <HeaderMobile session={session} />

      <div className="mx-auto flex h-full w-full max-w-375 flex-row overflow-hidden">
        {breadcrumbItems && <Breadcrumbs linksData={breadcrumbItems} />}
      </div>
    </>
  );
};

export default Navbar;
